import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useSearchParams } from 'react-router-dom';
import { ErrorBoundary } from '@sentry/react';
import { PLAYER_ID } from '@/consts/searchKeys';
import { filterAtoms, radarAtoms } from '@/consts/scope';
import { playerIdAtom } from '@/atoms/player/player';
import { useSetPlayerEventDataDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetPlayerEventDataDebouncedFilterParams';
import { sectionAtom } from '@/atoms/section';
import { GeneralErrorBoundaryFallback } from '../../errorBoundary/ErrorBoundaryFallback';
import { PlayerPreviewHeader } from './PlayerPreviewHeader';
import { PlayerPreviewBody } from './PlayerPreviewBody';

const SetPlayerPreviewData = () => {
  const [searchParams] = useSearchParams();
  const playerId = searchParams.get(PLAYER_ID);
  const setPlayerId = useSetAtom(playerIdAtom);
  const setSection = useSetAtom(sectionAtom);

  useEffect(() => {
    if (playerId) {
      setPlayerId(Number(playerId));
    }
  }, [playerId]);

  /**
   * Pretend that we're in the Player section so that the radar data is based on games the Player has played in,
   * and not the games that the user has filtered to in on *this* page (wherever we are).
   * Without this, a user's filter would affect the radar so the user could click on Harry Kane, then filter to Serie A
   * and the radar would be blank.
   * This is scoped to this component so that it doesn't affect the behaviour of the current page.
   */
  useEffect(() => {
    setSection('player');
  }, []);

  useSetPlayerEventDataDebouncedFilterParams(0);

  return null;
};

export const PlayerPreview = () => {
  const [searchParams] = useSearchParams();
  const playerId = searchParams.get(PLAYER_ID);

  if (!playerId) return null;

  return (
    <ScopeProvider atoms={[...filterAtoms, ...radarAtoms, sectionAtom, playerIdAtom]}>
      <SetPlayerPreviewData />
      <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
        <PlayerPreviewHeader />
        <PlayerPreviewBody />
      </ErrorBoundary>
    </ScopeProvider>
  );
};
